import type { ReactNode } from "react";
import {
  Banknote,
  BarChart3,
  BedDouble,
  BriefcaseBusiness,
  Building2,
  Bus,
  ChartColumnIncreasing,
  ClipboardCheck,
  ClipboardList,
  Database,
  FileText,
  FileUp,
  ArrowLeftRight,
  GitCompareArrows,
  HandCoins,
  Hotel,
  KeyRound,
  LayoutDashboard,
  ListTree,
  LogOut,
  MapPinned,
  Package2,
  Palette,
  Plane,
  QrCode,
  ReceiptText,
  Route,
  Share2,
  Sheet,
  RotateCw,
  Settings,
  ShieldCheck,
  Tags,
  Train,
  UserRound,
  UserRoundCog,
  Users,
  UtensilsCrossed,
  WalletCards,
  WandSparkles,
} from "lucide-react";

import { ITINERARY_PERMISSIONS } from "../../features/itinerary";

/**
 * 后台导航项配置。
 */
export interface AdminNavigationItem {
  key: string;
  path: string;
  label: string;
  pageTitle: string;
  pageDescription: string;
  breadcrumb: string[];
  icon: ReactNode;
  group: string;
  parentKeys?: string[];
  requiredPermission?: string;
}

/**
 * 后台导航配置中心。
 */
export const adminNavigationConfig: AdminNavigationItem[] = [
  {
    key: "workspace",
    path: "/workspace",
    label: "工作台",
    pageTitle: "工作台",
    pageDescription: "管理经营指标、业务动态和待处理事项。",
    breadcrumb: ["首页", "工作台"],
    icon: <LayoutDashboard size={18} />,
    group: "首页",
    requiredPermission: "workspace.dashboard:access",
  },
  {
    key: "crm",
    path: "/crm",
    label: "CRM 系统",
    pageTitle: "CRM 系统",
    pageDescription: "管理客户线索、跟进记录和商机转化。",
    breadcrumb: ["首页", "CRM 系统"],
    icon: <BriefcaseBusiness size={18} />,
    group: "业务系统",
  },
  {
    key: "crm-opportunity-workbench",
    path: "/crm/opportunities/workbench",
    label: "商机工作台",
    pageTitle: "商机工作台",
    pageDescription: "沉淀早期出行机会，跟进商机、候选定制、阶段负责人和阶段变化。",
    breadcrumb: ["首页", "CRM 系统", "商机工作台"],
    icon: <BriefcaseBusiness size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
    requiredPermission: "crm.opportunity:access",
  },
  {
    key: "customization-projects",
    path: "/crm/customization/projects",
    label: "定制列表",
    pageTitle: "定制列表",
    pageDescription: "查看定制项目、行程与成团状态，并进入工作台或订单交接。",
    breadcrumb: ["首页", "CRM 系统", "定制列表"],
    icon: <WandSparkles size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
    requiredPermission: "customization.project:list",
  },
  {
    key: "crm-contact-workbench",
    path: "/crm/contacts/workbench",
    label: "联系人工作台",
    pageTitle: "联系人工作台",
    pageDescription: "集中查看联系人、学校、归属销售和动态记录，并支持批量转移联系人归属销售。",
    breadcrumb: ["首页", "CRM 系统", "联系人工作台"],
    icon: <Users size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
    requiredPermission: "crm.contact:access",
  },
  {
    key: "crm-customer-workbench",
    path: "/crm/customers/workbench",
    label: "客户工作台",
    pageTitle: "客户工作台",
    pageDescription: "集中查看客户、组织单元、归属销售、商机占位和联系人数量，并支持批量分配客户归属销售。",
    breadcrumb: ["首页", "CRM 系统", "客户工作台"],
    icon: <Building2 size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
    requiredPermission: "crm.customer:access",
  },
  {
    key: "crm-collaboration-pools",
    path: "/crm/collaboration/pools",
    label: "任务池",
    pageTitle: "任务池",
    pageDescription: "查看电销、强跟等公共任务池，可认领有权限处理的联系人或客户。",
    breadcrumb: ["首页", "CRM 系统", "任务池"],
    icon: <ClipboardList size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
    requiredPermission: "crm.collaboration:access",
  },
  {
    key: "crm-basic-config",
    path: "/crm/basic-config",
    label: "基础配置",
    pageTitle: "基础配置",
    pageDescription: "集中维护区域经营、联系人导入合并与协作任务等 CRM 基础配置入口。",
    breadcrumb: ["首页", "CRM 系统", "基础配置"],
    icon: <Settings size={18} />,
    group: "业务系统",
    parentKeys: ["crm"],
  },
  {
    key: "sales-performance",
    path: "/sales-performance",
    label: "销售部绩效",
    pageTitle: "销售部绩效",
    pageDescription: "按月份查询并导出销售部十一项绩效指标。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "销售部绩效"],
    icon: <BriefcaseBusiness size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
    requiredPermission: "sales_performance.report:access",
  },
  {
    key: "crm-market-regions",
    path: "/crm/market-regions",
    label: "区域经营配置",
    pageTitle: "区域经营配置",
    pageDescription: "维护区域负责人、协管人、观察人和区域覆盖范围，作为联系人权限分配的配置来源。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "区域经营配置"],
    icon: <MapPinned size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
    requiredPermission: "crm.region_config:access",
  },
  {
    key: "crm-contact-import",
    path: "/crm/contacts/import",
    label: "联系人批量导入",
    pageTitle: "联系人批量导入",
    pageDescription: "上传或粘贴表格，实时查重并确认后批量写入联系人和动态。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "联系人批量导入"],
    icon: <FileUp size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
    requiredPermission: "crm.contact_import:access",
  },
  {
    key: "crm-contact-merge",
    path: "/crm/contacts/merge",
    label: "重复联系人合并",
    pageTitle: "重复联系人合并",
    pageDescription: "审核并执行多个重复联系人合并到一个保留联系人，完整记录字段选择和两库迁移审计。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "重复联系人合并"],
    icon: <GitCompareArrows size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
    requiredPermission: "crm.contact_merge:access",
  },
  {
    key: "crm-collaboration-tasks",
    path: "/crm/collaboration/tasks",
    label: "协作任务",
    pageTitle: "协作任务",
    pageDescription: "创建电销、强跟等临时权限协作任务，处理任务池认领与权限释放。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "协作任务"],
    icon: <BriefcaseBusiness size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
    requiredPermission: "crm.collaboration:access",
  },
  {
    key: "school-academy-major",
    path: "/crm/school-academy-major",
    label: "校院系专业",
    pageTitle: "校院系专业",
    pageDescription: "维护学校、院系与专业组织基础数据。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "校院系专业"],
    icon: <Building2 size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config"],
  },
  {
    key: "crm-organizations",
    path: "/crm/school-academy-major/school-organizations",
    label: "学校组织",
    pageTitle: "学校组织",
    pageDescription: "维护学校主数据和多个校区地址。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "校院系专业", "学校组织"],
    icon: <Building2 size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config", "school-academy-major"],
    requiredPermission: "system.organization:access",
  },
  {
    key: "organization-units",
    path: "/crm/school-academy-major/organization-units",
    label: "组织单元",
    pageTitle: "组织单元",
    pageDescription: "维护学校下的学院、系和专业固定深度组织结构。",
    breadcrumb: ["首页", "CRM 系统", "基础配置", "校院系专业", "组织单元"],
    icon: <ListTree size={18} />,
    group: "业务系统",
    parentKeys: ["crm", "crm-basic-config", "school-academy-major"],
    requiredPermission: "system.organization:access",
  },
  {
    key: "digital-ops",
    path: "/digital-ops",
    label: "行中数字化系统",
    pageTitle: "行中数字化系统",
    pageDescription: "承载研学过程中的运营协同和数字化流程。",
    breadcrumb: ["首页", "行中数字化系统"],
    icon: <ChartColumnIncreasing size={18} />,
    group: "业务系统",
  },
  {
    key: "product-lines",
    path: "/product-lines",
    label: "课程线路",
    pageTitle: "课程线路",
    pageDescription: "配置线路产品和研学服务标准。",
    breadcrumb: ["首页", "课程线路"],
    icon: <ListTree size={18} />,
    group: "业务系统",
  },
  {
    key: "course-routes",
    path: "/product-lines/course-routes",
    label: "课程线路管理",
    pageTitle: "课程线路管理",
    pageDescription: "查看全部行程或本人负责项目的行程，并进入行程编辑、核价或确认件。",
    breadcrumb: ["首页", "课程线路", "课程线路管理"],
    icon: <Route size={18} />,
    group: "业务系统",
    parentKeys: ["product-lines"],
    requiredPermission: ITINERARY_PERMISSIONS.access,
  },
  {
    key: "itinerary-my-shares",
    path: "/itineraries/my-shares",
    label: "我的分享",
    pageTitle: "我的分享",
    pageDescription: "",
    breadcrumb: ["首页", "课程线路", "我的分享"],
    icon: <Share2 size={18} />,
    group: "业务系统",
    parentKeys: ["product-lines"],
    requiredPermission: ITINERARY_PERMISSIONS.mySharesAccess,
  },
  {
    key: "supply-chain",
    path: "/supply-chain",
    label: "供应链管理",
    pageTitle: "供应链管理",
    pageDescription: "管理供应商协作、采购节点和资源对接。",
    breadcrumb: ["首页", "供应链管理"],
    icon: <Building2 size={18} />,
    group: "业务系统",
  },
  {
    key: "supply-chain-basic-settings",
    path: "/supply-chain/basic-settings",
    label: "供应商管理",
    pageTitle: "供应商管理",
    pageDescription: "维护地接社、车队、导游和酒店经营或签约主体。",
    breadcrumb: ["首页", "供应链管理", "供应商管理"],
    icon: <Settings size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "supply-chain-basic-settings-guides",
    path: "/supply-chain/basic-settings/guides",
    label: "导游",
    pageTitle: "导游",
    pageDescription: "维护导游供应商主数据与品类档案。",
    breadcrumb: ["首页", "供应链管理", "供应商管理", "导游"],
    icon: <UserRound size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain", "supply-chain-basic-settings"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "supply-chain-basic-settings-buses",
    path: "/supply-chain/basic-settings/buses",
    label: "车队",
    pageTitle: "车队",
    pageDescription: "维护车队供应商主数据与品类档案。",
    breadcrumb: ["首页", "供应链管理", "供应商管理", "车队"],
    icon: <Bus size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain", "supply-chain-basic-settings"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "supply-chain-basic-settings-restaurants",
    path: "/supply-chain/basic-settings/restaurants",
    label: "餐厅供应商",
    pageTitle: "餐厅供应商",
    pageDescription: "维护餐厅经营、签约或收款主体。",
    breadcrumb: ["首页", "供应链管理", "供应商管理", "餐厅供应商"],
    icon: <UtensilsCrossed size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain", "supply-chain-basic-settings"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "supply-chain-basic-settings-hotels",
    path: "/supply-chain/basic-settings/hotels",
    label: "酒店供应商（经营/签约主体）",
    pageTitle: "酒店供应商",
    pageDescription: "维护酒店经营、签约或收款主体，不维护酒店资源事实。",
    breadcrumb: ["首页", "供应链管理", "供应商管理", "酒店供应商"],
    icon: <Hotel size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain", "supply-chain-basic-settings"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "supply-chain-basic-settings-grounds",
    path: "/supply-chain/basic-settings/grounds",
    label: "地接社",
    pageTitle: "地接社",
    pageDescription: "维护地接社供应商主数据与品类档案。",
    breadcrumb: ["首页", "供应链管理", "供应商管理", "地接社"],
    icon: <MapPinned size={18} />,
    group: "业务系统",
    parentKeys: ["supply-chain", "supply-chain-basic-settings"],
    requiredPermission: "supply.basic_settings:access",
  },
  {
    key: "finance",
    path: "/finance",
    label: "财务结算系统",
    pageTitle: "财务结算系统",
    pageDescription: "统一处理财务结算、回款和对账流程。",
    breadcrumb: ["首页", "财务结算系统"],
    icon: <WalletCards size={18} />,
    group: "经营管理",
  },
  {
    key: "finance-workbench",
    path: "/finance/workbench",
    label: "财务工作台",
    pageTitle: "财务工作台",
    pageDescription: "全部团的应收总额和真实应付，以及轮到我审批、待付款、还有可请额度的团的件数，点进去就是筛好的页面。",
    breadcrumb: ["首页", "财务结算系统", "财务工作台"],
    icon: <LayoutDashboard size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "finance.workbench:access",
  },
  {
    key: "finance-payable-list",
    path: "/finance/payable-list",
    label: "应付列表",
    pageTitle: "应付列表",
    pageDescription: "有确认件或请过款的行程一行，看真实应付、已请款、已付、未付，合计算全部筛选结果。",
    breadcrumb: ["首页", "财务结算系统", "应付列表"],
    icon: <Sheet size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "finance.payable_list:access",
  },
  {
    key: "finance-payables",
    path: "/finance/payables",
    label: "应付清单",
    pageTitle: "应付清单",
    pageDescription: "按团查看每个结算主体的当前应付和团合计。",
    breadcrumb: ["首页", "财务结算系统", "应付清单"],
    icon: <ReceiptText size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "fulfillment.order:access",
  },
  {
    key: "finance-receivable-list",
    path: "/finance/receivable-list",
    label: "应收列表",
    pageTitle: "应收列表",
    pageDescription: "全部团的当前个人应收，按团编码、出团日期、销售筛选，合计算全部筛选结果。",
    breadcrumb: ["首页", "财务结算系统", "应收列表"],
    icon: <Sheet size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "finance.receivable_list:access",
  },
  {
    key: "finance-receivables",
    path: "/finance/receivables",
    label: "应收清单",
    pageTitle: "应收清单",
    pageDescription: "按团编码查团，看名单定稿后的应收合计与订单头合同总额的差额。",
    breadcrumb: ["首页", "财务结算系统", "应收清单"],
    icon: <Banknote size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "customization.order_handover:access",
  },
  {
    key: "finance-payment-requests",
    path: "/finance/payment-requests",
    label: "请款记录",
    pageTitle: "请款记录",
    pageDescription: "每笔请款的金额、状态和当前审批人，点开看全部字段、附件和审批记录。",
    breadcrumb: ["首页", "财务结算系统", "请款记录"],
    icon: <HandCoins size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
    requiredPermission: "finance.payment_request:access",
  },
  {
    key: "approval-todo",
    path: "/approval/todo",
    label: "待我审批",
    pageTitle: "待我审批",
    pageDescription: "轮到我审批的单子和我处理过的单子，在这里逐级通过或驳回。",
    breadcrumb: ["首页", "财务结算系统", "待我审批"],
    icon: <ClipboardCheck size={18} />,
    group: "经营管理",
    parentKeys: ["finance"],
  },
  {
    key: "insights",
    path: "/insights",
    label: "数据智能",
    pageTitle: "数据智能",
    pageDescription: "查看经营分析、预测结果和专项数据洞察。",
    breadcrumb: ["首页", "数据智能"],
    icon: <BarChart3 size={18} />,
    group: "经营管理",
  },
  {
    key: "data-governance",
    path: "/data-governance",
    label: "数据治理",
    pageTitle: "数据治理",
    pageDescription: "管理数据同步、数据质量和历史数据治理任务。",
    breadcrumb: ["首页", "数据治理"],
    icon: <GitCompareArrows size={18} />,
    group: "经营管理",
  },
  {
    key: "legacy-sync",
    path: "/data-governance/legacy-sync",
    label: "旧系统数据同步",
    pageTitle: "旧系统数据同步",
    pageDescription: "预览并执行旧系统历史数据到新系统的单向同步任务。",
    breadcrumb: ["首页", "数据治理", "旧系统数据同步"],
    icon: <GitCompareArrows size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.legacy_sync:access",
  },
  {
    key: "crm-legacy-sync",
    path: "/data-governance/crm-legacy-sync",
    label: "CRM数据同步到旧系统",
    pageTitle: "CRM数据同步到旧系统",
    pageDescription: "查看学校、组织单元、联系人、住宿点和考察点写回旧系统的 Outbox 与同步详情。",
    breadcrumb: ["首页", "数据治理", "CRM数据同步到旧系统"],
    icon: <ArrowLeftRight size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.crm_legacy_sync:access",
  },
  {
    key: "legacy-sync-test",
    path: "/data-governance/legacy-sync-test",
    label: "数据同步到测试库",
    pageTitle: "数据同步到测试库",
    pageDescription: "预览并执行旧系统测试库数据到新系统测试库的单向同步任务。",
    breadcrumb: ["首页", "数据治理", "数据同步到测试库"],
    icon: <GitCompareArrows size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.legacy_sync:access",
  },
  {
    key: "chroma-vector-sync",
    path: "/data-governance/chroma-vector-sync",
    label: "同步向量库",
    pageTitle: "同步向量库",
    pageDescription: "全量重建 Chroma 向量库 embedding 数据。",
    breadcrumb: ["首页", "数据治理", "同步向量库"],
    icon: <Database size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.chroma_vector:access",
  },
  {
    key: "resource-profile-rebuild",
    path: "/data-governance/resource-profile-rebuild",
    label: "资源画像重算",
    pageTitle: "资源画像重算",
    pageDescription: "根据新系统行程与需求数据，重算考察点画像和住宿点使用统计。",
    breadcrumb: ["首页", "数据治理", "资源画像重算"],
    icon: <RotateCw size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.resource_profile:access",
  },
  {
    key: "geo-governance",
    path: "/data-governance/geo",
    label: "GEO 历史治理",
    pageTitle: "GEO 历史治理",
    pageDescription: "批量补齐历史 GEO；资源酒店采用 LLM 经营地址与高德地理编码，其他对象沿用各自规则。",
    breadcrumb: ["首页", "数据治理", "GEO 历史治理"],
    icon: <MapPinned size={18} />,
    group: "经营管理",
    parentKeys: ["data-governance"],
    requiredPermission: "data.geo_governance:access",
  },
  {
    key: "demo-sqlserver",
    path: "/demo/sqlserver",
    label: "数据库 Demo",
    pageTitle: "数据库连通性 Demo",
    pageDescription: "演示统一请求链路下的数据库连通性与可观测性。",
    breadcrumb: ["首页", "数据库 Demo"],
    icon: <Database size={18} />,
    group: "技术验证",
  },
  {
    key: "demo-mysql-query-list",
    path: "/demo/mysql-query-list",
    label: "MySQL 查询列表",
    pageTitle: "通用演示列表",
    pageDescription: "演示单选、多选、日期区间与分页查询。",
    breadcrumb: ["首页", "MySQL 查询列表"],
    icon: <Database size={18} />,
    group: "技术验证",
  },
  {
    key: "demo-mysql-query-list-demo2",
    path: "/demo/mysql-query-list-demo2",
    label: "MySQL 查询列表 Demo2",
    pageTitle: "通用演示列表 Demo2",
    pageDescription: "演示 Demo2 列表查询与分页筛选。",
    breadcrumb: ["首页", "MySQL 查询列表 Demo2"],
    icon: <Database size={18} />,
    group: "技术验证",
  },
  {
    key: "payment-validation",
    path: "/payment-validation/collections",
    label: "收付款验证",
    pageTitle: "收付款验证",
    pageDescription: "验证二维码与银行卡号收款、付款明细及退款处理。",
    breadcrumb: ["首页", "技术验证", "收付款验证"],
    icon: <QrCode size={18} />,
    group: "技术验证",
    requiredPermission: "payment_validation.collection:access",
  },
  {
    key: "settings",
    path: "/settings",
    label: "基础设置",
    pageTitle: "基础设置",
    pageDescription: "维护系统基础配置、组织信息和角色约束。",
    breadcrumb: ["首页", "基础设置"],
    icon: <Settings size={18} />,
    group: "系统管理",
  },
  {
    key: "org-workflow",
    path: "/settings/org-workflow",
    label: "组织权限工作流",
    pageTitle: "组织权限工作流",
    pageDescription: "维护组织架构、用户归属与审批汇报关系。",
    breadcrumb: ["首页", "基础设置", "组织权限工作流"],
    icon: <Users size={18} />,
    group: "系统管理",
    parentKeys: ["settings"],
  },
  {
    key: "auth-management",
    path: "/settings/auth",
    label: "登录/注册",
    pageTitle: "登录/注册",
    pageDescription: "管理当前账号密码与登录状态。",
    breadcrumb: ["首页", "基础设置", "登录/注册"],
    icon: <UserRoundCog size={18} />,
    group: "系统管理",
    parentKeys: ["settings"],
  },
  {
    key: "auth-reset-password",
    path: "/auth/reset-password",
    label: "重置密码",
    pageTitle: "重置密码",
    pageDescription: "重置当前登录管理员的账号密码。",
    breadcrumb: ["首页", "基础设置", "登录/注册", "重置密码"],
    icon: <KeyRound size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "auth-management"],
  },
  {
    key: "auth-logout",
    path: "/auth/logout",
    label: "退出登录",
    pageTitle: "退出登录",
    pageDescription: "清理当前登录态并返回登录页。",
    breadcrumb: ["首页", "基础设置", "登录/注册", "退出登录"],
    icon: <LogOut size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "auth-management"],
  },
  {
    key: "sys-dept-users",
    path: "/sys/dept-users",
    label: "组织和用户",
    pageTitle: "组织和用户",
    pageDescription: "维护部门树、用户资料、员工身份与主管关系。",
    breadcrumb: ["首页", "基础设置", "组织权限工作流", "组织和用户"],
    icon: <Users size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "org-workflow"],
    requiredPermission: "system.dept_user:access",
  },
  {
    key: "authz-roles",
    path: "/settings/org-workflow/roles",
    label: "角色设置",
    pageTitle: "角色设置",
    pageDescription: "统一配置角色成员、菜单页面与操作权限。",
    breadcrumb: ["首页", "基础设置", "组织权限工作流", "角色设置"],
    icon: <ShieldCheck size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "org-workflow"],
    requiredPermission: "system.role:access",
  },
  {
    key: "approval-settings",
    path: "/approval/settings",
    label: "审批设置",
    pageTitle: "审批设置",
    pageDescription: "配置每种审批逐级由谁审批，改设置只影响之后提交的单子。",
    breadcrumb: ["首页", "基础设置", "组织权限工作流", "审批设置"],
    icon: <ListTree size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "org-workflow"],
    requiredPermission: "approval.flow:access",
  },
  {
    key: "basic-dictionary",
    path: "/settings/basic-dictionary",
    label: "基础字典",
    pageTitle: "基础字典",
    pageDescription: "维护业务基础字典配置。",
    breadcrumb: ["首页", "基础设置", "基础字典"],
    icon: <Tags size={18} />,
    group: "系统管理",
    parentKeys: ["settings"],
  },
  {
    key: "dic-major",
    path: "/dic/majors",
    label: "标准专业字典",
    pageTitle: "标准专业字典",
    pageDescription: "维护标准专业、专业大类、拼音码与专业别名。",
    breadcrumb: ["首页", "基础设置", "基础字典", "标准专业字典"],
    icon: <Tags size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "basic-dictionary"],
    requiredPermission: "dictionary.major:access",
  },
  {
    key: "dic-bus-vehicle-type",
    path: "/dic/bus-vehicle-types",
    label: "大巴车型字典",
    pageTitle: "大巴车型字典",
    pageDescription: "维护人工核价可选车型的名称、座位数、排序与启停状态。",
    breadcrumb: ["首页", "基础设置", "基础字典", "大巴车型字典"],
    icon: <Tags size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "basic-dictionary"],
    requiredPermission: "dictionary.bus_vehicle_type:access",
  },
  {
    key: "dic-stay-area",
    path: "/dic/stay-areas",
    label: "住宿点字典",
    pageTitle: "住宿点字典",
    pageDescription: "维护住宿点名称、区域层级、拼音码与导航信息。",
    breadcrumb: ["首页", "基础设置", "基础字典", "住宿点字典"],
    icon: <Tags size={18} />,
    group: "系统管理",
    parentKeys: ["settings", "basic-dictionary"],
    requiredPermission: "dictionary.stay_area:access",
  },
  {
    key: "spu-sku",
    path: "/settings/spu-sku",
    label: "SPU+SKU",
    pageTitle: "SPU+SKU",
    pageDescription: "维护产品标准化基础配置。",
    breadcrumb: ["首页", "基础设置", "SPU+SKU"],
    icon: <Package2 size={18} />,
    group: "系统管理",
    parentKeys: ["settings"],
  },
  {
    key: "resources",
    path: "/resources",
    label: "资源中心",
    pageTitle: "资源中心",
    pageDescription: "管理项目资源、模版和文件资产。",
    breadcrumb: ["首页", "资源中心"],
    icon: <Package2 size={18} />,
    group: "系统管理",
  },
  {
    key: "resource-points-enterprise",
    path: "/resources/points/enterprise",
    label: "企业",
    pageTitle: "企业",
    pageDescription: "维护企业类考察点 SPU 及其票务与执行 SKU。",
    breadcrumb: ["首页", "资源中心", "企业"],
    icon: <Building2 size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.enterprise:access",
  },
  {
    key: "resource-points-intangible",
    path: "/resources/points/intangible",
    label: "非遗",
    pageTitle: "非遗",
    pageDescription: "维护非遗类考察点 SPU 及其票务与执行 SKU。",
    breadcrumb: ["首页", "资源中心", "非遗"],
    icon: <Palette size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.intangible:access",
  },
  {
    key: "resource-points-other",
    path: "/resources/points/other",
    label: "考察点",
    pageTitle: "考察点",
    pageDescription: "维护除企业、非遗和交通点位以外的其他考察点 SPU 及其票务与执行 SKU。",
    breadcrumb: ["首页", "资源中心", "考察点"],
    icon: <MapPinned size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.point:access",
  },
  {
    key: "resource-point-merge",
    path: "/resources/points/merge",
    label: "资源合并",
    pageTitle: "资源合并",
    pageDescription: "将重复的考察点、非遗和企业合并为同一资源。",
    breadcrumb: ["首页", "资源中心", "资源合并"],
    icon: <MapPinned size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.point_merge:access",
  },
  {
    key: "resource-points-railway",
    path: "/resources/points/railway",
    label: "火车站",
    pageTitle: "火车站",
    pageDescription: "维护火车站点位 SPU 及其票务与执行 SKU。",
    breadcrumb: ["首页", "资源中心", "火车站"],
    icon: <Train size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.railway_station:access",
  },
  {
    key: "railway-query",
    path: "/railway-query",
    label: "铁路查询",
    pageTitle: "铁路查询",
    pageDescription: "查询列车时刻、余票与席别票价。",
    breadcrumb: ["首页", "资源中心", "铁路查询"],
    icon: <Train size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "railway.query:access",
  },
  {
    key: "resource-points-airport",
    path: "/resources/points/airport",
    label: "机场",
    pageTitle: "机场",
    pageDescription: "维护机场点位 SPU 及其票务与执行 SKU。",
    breadcrumb: ["首页", "资源中心", "机场"],
    icon: <Plane size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.airport:access",
  },
  {
    key: "resource-stays",
    path: "/resources/stays",
    label: "房资源",
    pageTitle: "房资源",
    pageDescription: "管理酒店主档、房型、展示媒体、GEO 与供应关系。",
    breadcrumb: ["首页", "资源中心", "房资源"],
    icon: <BedDouble size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.stay:access",
  },
  {
    key: "hotel-map",
    path: "/resources/stay-map",
    label: "酒店地图",
    pageTitle: "酒店地图",
    pageDescription: "查看已有坐标的房资源酒店、GEO 状态及参考价格。",
    breadcrumb: ["首页", "资源中心", "酒店地图"],
    icon: <MapPinned size={18} />,
    group: "系统管理",
    parentKeys: ["resources"],
    requiredPermission: "resource.stay_map:access",
  },
];

/**
 * 按当前用户实时权限过滤菜单，并保留所有可见叶子项的祖先节点。
 */
export function filterAdminNavigationConfig(
  items: AdminNavigationItem[],
  user: { permissions?: string[]; isSuperuser?: boolean } | null | undefined,
): AdminNavigationItem[] {
  if (user?.isSuperuser) return items;
  const permissions = new Set(user?.permissions ?? []);
  const parentKeys = new Set(items.flatMap((item) => item.parentKeys ?? []));
  const visibleKeys = new Set<string>();
  for (const item of items) {
    if (parentKeys.has(item.key)) continue;
    if (!item.requiredPermission || permissions.has(item.requiredPermission)) {
      visibleKeys.add(item.key);
      for (const parentKey of item.parentKeys ?? []) visibleKeys.add(parentKey);
    }
  }
  return items.filter((item) => visibleKeys.has(item.key));
}

/**
 * 根据分组输出菜单结构。
 */
export function getAdminNavigationGroups(items: AdminNavigationItem[] = adminNavigationConfig) {
  return items.reduce<Array<{ group: string; items: AdminNavigationItem[] }>>(
    (groups, item) => {
      const currentGroup = groups.find((group) => group.group === item.group);
      if (currentGroup) {
        currentGroup.items.push(item);
        return groups;
      }

      groups.push({ group: item.group, items: [item] });
      return groups;
    },
    [],
  );
}

/**
 * 按 pathname 匹配导航项，供侧栏与页签图标复用。
 * 动态详情路由回落到对应菜单入口；未命中时返回 undefined。
 */
export function matchAdminNavigationItem(
  pathname: string,
  items: AdminNavigationItem[] = adminNavigationConfig,
): AdminNavigationItem | undefined {
  const normalized =
    pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname || "/";
  const specialKey = resolveAdminNavigationKeyForDynamicPath(normalized);
  if (specialKey) {
    return items.find((item) => item.key === specialKey);
  }
  return (
    items.find((item) => normalized === item.path) ??
    [...items]
      .sort((left, right) => right.path.length - left.path.length)
      .find(
        (item) =>
          item.path !== "/workspace" &&
          (normalized === item.path || normalized.startsWith(`${item.path}/`)),
      )
  );
}

/** 解析页签图标；未配置菜单时回退通用文档图标。 */
export function resolveAdminNavigationIcon(
  pathname: string,
  items: AdminNavigationItem[] = adminNavigationConfig,
): ReactNode {
  return matchAdminNavigationItem(pathname, items)?.icon ?? <FileText size={18} />;
}

/** 将无独立菜单项的动态路由映射到最近菜单 key。 */
function resolveAdminNavigationKeyForDynamicPath(pathname: string): string | undefined {
  if (
    /^\/crm\/customization\/projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname) ||
    /^\/crm\/opportunities\/[^/]+\/customization-projects\/[^/]+\/workbench(?:\/.*)?$/.test(pathname) ||
    /^\/crm\/order-handover\/[^/]+\/?$/.test(pathname)
  ) {
    return "customization-projects";
  }
  if (/^\/quote\/budgets\/[^/]+$/.test(pathname) || /^\/quote\/customer-quotes\/[^/]+$/.test(pathname)) {
    return "crm";
  }
  if (
    /^\/supply-chain\/fulfillment-orders\/workspaces\/[^/]+$/.test(pathname) ||
    /^\/supply-chain\/fulfillment-orders\/itineraries\/[^/]+(?:\/enter)?$/.test(pathname)
  ) {
    return "fulfillment-orders";
  }
  return undefined;
}
